<template>
  <div>
    <nav
      class="bg-green-700 text-white p-3 sm:p-4 md:p-3 lg:p-4 fixed top-0 left-0 right-0 z-50 shadow-lg transition-all duration-300"
      :class="{ 'shadow-xl': scrolled }"
    >
      <div
        class="container mx-auto px-2 md:px-4 lg:px-6 flex flex-wrap items-center justify-between"
      >
        <!-- Logo Section - Responsive sizing and clickable link to home -->
        <router-link to="/" class="flex items-center">
          <img
            :src="filmaLogo"
            alt="Filma Logo"
            class="h-8 w-8 sm:h-9 sm:w-9 md:h-9 md:w-9 lg:h-10 lg:w-10 mr-2 text-white transition-transform duration-500 transform hover:rotate-12 hover:scale-110"
          />
          <span class="text-xl sm:text-2xl md:text-2xl lg:text-3xl font-bold"
            >FILMA</span
          >
        </router-link>

        <!-- Desktop and Tablet Navigation Links - Visible from medium screens up -->
        <div
          class="hidden md:flex space-x-2 md:space-x-3 lg:space-x-6 xl:space-x-8"
        >
          <router-link
            to="/"
            class="py-2 px-2 md:px-3 lg:px-4 relative group transition-all duration-300 flex items-center text-sm md:text-sm lg:text-base"
            :class="[
              $route.path === '/'
                ? 'font-medium text-white border-b-2 border-white'
                : '',
            ]"
          >
            <i class="fas fa-home mr-1 md:mr-1.5 lg:mr-2"></i> <span>Home</span>
          </router-link>

          <!-- Portals Dropdown -->
          <div class="relative" ref="portalsMenu">
            <button
              @click="portalsOpen = !portalsOpen"
              class="py-2 px-2 md:px-3 lg:px-4 relative group transition-all duration-300 flex items-center text-sm md:text-sm lg:text-base"
              :class="[
                portalsOpen
                  ? 'font-medium text-white border-b-2 border-white'
                  : '',
              ]"
            >
              <i class="fas fa-th-large mr-1 md:mr-1.5 lg:mr-2"></i>
              <span>Portals</span>
              <i
                class="fas fa-chevron-down ml-1.5 text-xs transition-transform duration-300"
                :class="{ 'rotate-180': portalsOpen }"
              ></i>
            </button>

            <div
              v-if="portalsOpen"
              class="absolute right-0 mt-2 w-64 bg-white text-gray-800 rounded-lg shadow-xl overflow-hidden animate-slideDown z-50"
            >
              <a
                v-for="(portal, index) in portals"
                :key="index"
                :href="portal.url"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center px-4 py-3 hover:bg-green-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0"
                @click="portalsOpen = false"
              >
                <i :class="['fas', portal.icon, 'w-6 text-green-700']"></i>
                <span class="ml-2">
                  <span class="block font-medium">{{ portal.name }}</span>
                  <span class="block text-xs text-gray-500">{{
                    portal.description
                  }}</span>
                </span>
              </a>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Toggle Button -->
        <div class="flex md:hidden items-center space-x-2 pr-3">
          <!-- Mobile Menu Toggle -->
          <button
            class="p-2 rounded-lg hover:bg-green-800 transition-colors duration-200"
            @click="toggleMenu"
            aria-label="Toggle menu"
          >
            <div class="w-6 h-5 flex flex-col justify-between overflow-hidden">
              <span
                class="w-full h-0.5 bg-white rounded transform transition-transform duration-300"
                :class="{ 'rotate-45 translate-y-2': mobileMenuOpen }"
              ></span>
              <span
                class="w-full h-0.5 bg-white rounded transition-opacity duration-300"
                :class="{ 'opacity-0': mobileMenuOpen }"
              ></span>
              <span
                class="w-full h-0.5 bg-white rounded transform transition-transform duration-300"
                :class="{ '-rotate-45 -translate-y-2': mobileMenuOpen }"
              ></span>
            </div>
          </button>
        </div>
      </div>
    </nav>

    <!-- Spacer to prevent content from being hidden under the fixed navbar - Responsive height -->
    <div class="h-14 sm:h-16 md:h-15 lg:h-16"></div>

    <!-- Overlay for Mobile Menu -->
    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
      @click="mobileMenuOpen = false"
    ></div>

    <!-- Mobile Menu (Slide Down from Top) -->
    <div
      v-if="mobileMenuOpen"
      class="fixed top-14 sm:top-16 md:top-15 left-0 right-0 z-40 md:hidden bg-green-800 text-white shadow-xl transform animate-slideDown overflow-y-auto max-h-[calc(100vh-4rem)]"
    >
      <div class="container mx-auto px-4 py-2">
        <!-- Menu Items -->
        <router-link
          to="/"
          class="flex items-center py-3 px-4 hover:bg-green-700 rounded-lg transition-colors duration-200 border-l-2 border-transparent"
          :class="[$route.path === '/' ? 'border-white bg-green-700/50' : '']"
          @click="mobileMenuOpen = false"
        >
          <i class="fas fa-home w-6"></i>
          <span class="ml-2">Home</span>
        </router-link>

        <!-- Portals -->
        <div class="mt-2 pt-2 border-t border-green-700">
          <p class="px-4 pb-1 text-xs uppercase tracking-wide text-green-300">
            Our Portals
          </p>
          <a
            v-for="(portal, index) in portals"
            :key="index"
            :href="portal.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center py-3 px-4 hover:bg-green-700 rounded-lg transition-colors duration-200 border-l-2 border-transparent"
            @click="mobileMenuOpen = false"
          >
            <i :class="['fas', portal.icon, 'w-6']"></i>
            <span class="ml-2">{{ portal.name }}</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import filmaLogo from "@/assets/images/filma-logo.svg";

export default {
  name: "AppNavigation",
  data() {
    return {
      filmaLogo,
      mobileMenuOpen: false,
      scrolled: false,
      portalsOpen: false,
      portals: [
        {
          name: "Partners Portal",
          url: "https://partners.lastmilefinance.app",
          icon: "fa-handshake",
          description: "Manage partners and program setup",
        },
        {
          name: "VSLA Portal",
          url: "https://vsla.lastmilefinance.app",
          icon: "fa-users",
          description: "Attendance, savings, insurance and loans",
        },
        {
          name: "Institutions Portal",
          url: "https://institutions.lastmilefinance.app",
          icon: "fa-building",
          description: "Microfinance, SCIs, insurers and fund managers",
        },
      ],
    };
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleResize);

    // Add Font Awesome CSS
    if (!document.getElementById("font-awesome-css")) {
      const link = document.createElement("link");
      link.id = "font-awesome-css";
      link.rel = "stylesheet";
      link.href =
        "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css";
      document.head.appendChild(link);
    }

    // Ensure proper scrolling on initial load
    this.resetBodyScroll();
  },
  unmounted() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    this.resetBodyScroll(); // Ensure scroll is reset when component is destroyed
  },
  methods: {
    toggleMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      this.updateBodyClass();
    },

    handleClickOutside(event) {
      // Close the portals dropdown if clicked outside
      if (
        this.portalsOpen &&
        this.$refs.portalsMenu &&
        !this.$refs.portalsMenu.contains(event.target)
      ) {
        this.portalsOpen = false;
      }
    },

    handleScroll() {
      this.scrolled = window.scrollY > 10;
    },

    handleResize() {
      // Close mobile menu on larger screens
      if (window.innerWidth >= 768 && this.mobileMenuOpen) {
        this.mobileMenuOpen = false;
        this.resetBodyScroll();
      }
    },
    resetBodyScroll() {
      if (typeof document !== "undefined") {
        document.body.classList.remove("overflow-hidden");
        document.body.style.overflow = "auto";
        document.body.style.position = "static";
      }
    },

    updateBodyClass() {
      if (this.mobileMenuOpen) {
        document.body.classList.add("overflow-hidden");
        document.body.style.overflow = "hidden";
      } else {
        this.resetBodyScroll();
      }
    },
  },
  watch: {
    $route() {
      // Reset everything when route changes
      this.mobileMenuOpen = false;
      this.portalsOpen = false;
      this.resetBodyScroll();
    },
  },
};
</script>

<style scoped>
/* Animations */
.animate-slideDown {
  animation: slideDown 0.3s ease-in-out forwards;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

button:focus {
  @apply outline-none ring-2 ring-white/30;
}

/* Sticky Nav Scroll Transition */
.fixed {
  transition: all 0.3s ease;
}

/* Prevent content from being cut off on iOS devices */
.overflow-y-auto {
  -webkit-overflow-scrolling: touch;
}

/* Backdrop blur for mobile menu overlay */
.backdrop-blur-sm {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* Ensure navigation is visible on notched devices */
@supports (padding-top: env(safe-area-inset-top)) {
  .fixed.top-0 {
    padding-top: max(0.75rem, env(safe-area-inset-top));
  }

  .fixed.right-0 {
    padding-right: env(safe-area-inset-right);
  }
}

/* Responsive containers for different device sizes */
@media (min-width: 640px) and (max-width: 767px) {
  .container {
    max-width: 640px;
  }
}

/* Better tablet portrait handling */
@media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
  .md\:flex {
    flex-wrap: nowrap;
    justify-content: space-between;
  }

  .md\:flex > a {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .container {
    max-width: 768px;
  }

  /* Better spacing for tablet navigation */
  .md\:flex > * {
    margin-right: 0.75rem;
  }
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .container {
    max-width: 1024px;
  }
}
</style>
